import type { ColorName } from '@emotion/react';
import { getReferenceId } from '@quromedical/fhir-common';
import type {
  ObservationResultsResponse,
  PathologyReportGetAllResponse,
} from '@quromedical/models';
import { compact, flatten } from '@quromedical/utils';
import { AsyncDownloadButton, LinkButton } from 'components/input';
import type { ColumnType } from 'components/table';
import { getDisplayDate } from 'core/display';
import { TaskColumn } from 'core/task';
import { Text } from 'design-system';
import type { Attachment } from 'fhir/r4';
import { DiagnosticReportApi } from 'integration';
import { strings } from 'strings';

interface PathologyReportTable {
  orderNumber: string;
  reportStatus: string;
  reportDate: string;
  report: Attachment[];
  id: string;
  task?: undefined;
  serviceRequestsId: string;
}

interface PathologyResultReportTable {
  name: string;
  interpretation: string;
  result: string;
  referenceRange: string;
  id: string;
}

const api = new DiagnosticReportApi();

const fetchUrl =
  (reportId: string) => async (): Promise<string | undefined> => {
    if (!reportId) {
      return undefined;
    }

    const presentedForm = await api.getPresentedForm(reportId);

    const pdf = presentedForm.find((p) => p.contentType === 'application/zip');

    return pdf?.key;
  };

export const pathologyReportColumns = (
  patientId: string,
): ColumnType<PathologyReportTable>[] => [
  {
    header: strings.PathologyOrderNumberSubheading,
    accessor: 'orderNumber',
  } as ColumnType<PathologyReportTable, 'orderNumber'>,
  {
    header: strings.PathologyReportDateSubheading,
    accessor: 'reportDate',
  } as ColumnType<PathologyReportTable, 'reportDate'>,
  {
    header: strings.PathologyReportStatusSubheading,
    accessor: 'reportStatus',
  } as ColumnType<PathologyReportTable, 'reportStatus'>,
  {
    header: strings.MediaDownloadButtonText,
    Cell: ({ row }) => (
      <AsyncDownloadButton
        fetchUrl={fetchUrl(row.original.id)}
        title={strings.PathologyDownloadButtonTitle}
      />
    ),
    accessor: 'report',
  } as ColumnType<PathologyReportTable, 'report'>,
  {
    header: strings.PathologyViewReport,
    Cell: ({ row }) => (
      <LinkButton
        text={strings.PathologyTestResults}
        href={`/patient/${patientId}/service-request/${row.original.serviceRequestsId}`}
      />
    ),

    accessor: 'serviceRequestsId',
  } as ColumnType<PathologyReportTable, 'serviceRequestsId'>,
  {
    accessor: 'task',
    header: '',
    Cell: ({ row }) => (
      <TaskColumn
        patientId={patientId}
        focusId={row.original.id}
        focusType="DiagnosticReport"
      ></TaskColumn>
    ),
  } as ColumnType<PathologyReportTable, 'task'>,
];

const mapToPathologyReportTable = (
  res: ObservationResultsResponse,
): PathologyResultReportTable[] => {
  const table = res.results?.map((report) => {
    return report.components.map<PathologyResultReportTable>((component) => {
      const result: PathologyResultReportTable = {
        name: component.code || '',
        interpretation: component.interpretation || '',
        result: component.valueQuantity.value || component?.valueString || '',
        referenceRange: component.referenceRange || '',
        id: report.id,
      };
      return result;
    });
  });

  return flatten(compact(table));
};

export const pathologyReportWithResultsColumns =
  (): ColumnType<PathologyResultReportTable>[] => [
    {
      header: strings.PathologyOrderNameSubheading,
      accessor: 'name',
    } as ColumnType<PathologyResultReportTable, 'name'>,
    {
      header: strings.PathologyOrderInterpretationSubheading,
      Cell: ({ row }) => (
        <Text
          color={
            interpretationColorMap[
              displayInterpretationColor(row.original.interpretation)
            ]
          }
        >
          {row.original.interpretation}
        </Text>
      ),
      accessor: 'interpretation',
    } as ColumnType<PathologyResultReportTable, 'interpretation'>,
    {
      header: strings.PathologyOrderResultSubheading,
      accessor: 'result',
    } as ColumnType<PathologyResultReportTable, 'result'>,
    {
      header: strings.PathologyOrderReferenceRangeSubheading,
      accessor: 'referenceRange',
    } as ColumnType<PathologyResultReportTable, 'referenceRange'>,
  ];

export const getDataPathologyTestResultFromResponse = (
  res: ObservationResultsResponse,
): Partial<PathologyResultReportTable>[] => {
  return mapToPathologyReportTable(res);
};

export const getDataFromResponse = (
  res: PathologyReportGetAllResponse,
): Partial<PathologyReportTable>[] => {
  return res.reports.map((report) => ({
    id: report.id,
    orderNumber: report.orderNumber,
    reportStatus: report.status,
    reportDate: getDisplayDate(report.issued),
    report: report.reports,
    serviceRequestsId: getReferenceId(report.order),
  }));
};
export const interpretationColorMap: Record<string, ColorName> = {
  Low: 'risk-medium',
  High: 'risk-medium',
  Normal: 'risk-low',
  Significant: 'risk-high',
  Significantly: 'risk-high',
};

const displayInterpretationColor = (input: string): string => {
  const matchedWord = input
    .split(' ')
    .find((word) => word in interpretationColorMap);
  return matchedWord ?? '';
};
